import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSession } from '../auth/useSession.js';

type Props = {
  children: ReactNode;
  /** When set, the signed-in user must hold this capability or they get a plain refusal. */
  requireCapability?: 'manageVendors' | 'authorSurveys' | 'manageRoster';
  /** Where to send signed-out visitors. Defaults to the magic-link login; admin routes use their own. */
  loginPath?: string;
};

export function RequireAuth({ children, requireCapability, loginPath = '/login' }: Props) {
  const session = useSession();
  const location = useLocation();

  if (session.isPending) return <main>Loading…</main>;

  if (!session.data) {
    // Preserve where they were headed so sign-in returns them to it.
    const redirectTo = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`${loginPath}?redirectTo=${redirectTo}`} replace />;
  }

  if (requireCapability && !session.data.capabilities[requireCapability]) {
    return (
      <main>
        <h1>Not available</h1>
        <p>Your account does not have access to this page.</p>
      </main>
    );
  }

  return <>{children}</>;
}
